// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Authentication from '@app/features/auth/state/Authentication';
import * as ChannelCommands from '@app/features/channel/commands/ChannelCommands';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import {ChannelSettingsModal} from '@app/features/channel/components/modals/ChannelSettingsModal';
import {EditGroupModal} from '@app/features/channel/components/modals/EditGroupModal';
import {GroupInvitesModal} from '@app/features/channel/components/modals/GroupInvitesModal';
import type {Channel} from '@app/features/channel/models/Channel';
import {ChannelDebugModal} from '@app/features/devtools/components/debug/ChannelDebugModal';
import {GuildNotificationSettingsModal} from '@app/features/guild/components/modals/GuildNotificationSettingsModal';
import {useLeaveGroup} from '@app/features/guild/hooks/useLeaveGroup';
import type {Guild} from '@app/features/guild/models/Guild';
import GuildNSFWAgree from '@app/features/guild/state/GuildNSFWAgree';
import * as InviteCommands from '@app/features/invite/commands/InviteCommands';
import {InviteModal} from '@app/features/invite/components/modals/InviteModal';
import * as InviteUtils from '@app/features/invite/utils/InviteUtils';
import Favorites from '@app/features/messaging/state/Favorites';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import {buildChannelLink} from '@app/features/messaging/utils/MessageLinkUtils';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import ReadStates from '@app/features/read_state/state/ReadStates';
import {
	CloseDMIcon,
	CopyIdIcon,
	CopyLinkIcon,
	DebugChannelIcon,
	DeleteIcon,
	EditGroupIcon,
	FavoriteIcon,
	InviteIcon,
	LeaveIcon,
	MarkAsReadIcon,
	MuteIcon,
	NotificationSettingsIcon,
	OpenLinkIcon,
	PinIcon,
	SendInvitesIcon,
	SettingsIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {MenuGroupType, MenuItemType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import UserSettings from '@app/features/user/state/UserSettings';
import {getMutedText} from '@app/lib/overlay/contextMenu';
import {ME} from '@app/vendor/constants/AppConstants';
import {ChannelTypes, Permissions} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {useMemo} from 'react';

const logger = new Logger('ChannelMenuData');
export interface ChannelMenuDataOptions {
	onClose: () => void;
	onOpenMuteSheet?: () => void;
}
export interface ChannelMenuData {
	groups: Array<MenuGroupType>;
	handlers: ChannelMenuHandlers;
	state: ChannelMenuState;
}
export interface ChannelMenuHandlers {
	handleMarkAsRead: () => void;
	handleToggleFavorite: () => void;
	handleInviteMembers: () => void;
	handleCopyChannelLink: () => Promise<void>;
	handleOpenChannelLink: () => void;
	handleOpenMuteSheet: () => void;
	handleNotificationSettings: () => void;
	handleChannelSettings: () => void;
	handleDeleteChannel: () => void;
	handleCopyChannelId: () => Promise<void>;
	handleEditGroup: () => void;
	handleShowInvites: () => void;
	handlePinChannel: () => Promise<void>;
	handleUnpinChannel: () => Promise<void>;
	handleLeaveGroup: () => void;
	handleCloseDM: () => void;
	handleDebugChannel: () => void;
	handleResetNSFWAgreeState: () => void;
}
export interface ChannelMenuState {
	isGroupDM: boolean;
	isDM: boolean;
	isTextChannel: boolean;
	isVoiceChannel: boolean;
	isLinkChannel: boolean;
	isOwner: boolean;
	isMuted: boolean;
	isFavorited: boolean;
	hasUnread: boolean;
	canManageChannels: boolean;
	canInvite: boolean;
	developerMode: boolean;
	isPinned: boolean;
	mutedText: string | undefined;
}
function getChannelMenuState(channel: Channel, guild: Guild | undefined): ChannelMenuState {
	const currentUserId = Authentication.currentUserId;
	const isGroupDM = channel.type === ChannelTypes.GROUP_DM;
	const isDM = channel.type === ChannelTypes.DM;
	const isTextChannel = channel.type === ChannelTypes.GUILD_TEXT;
	const isVoiceChannel = channel.type === ChannelTypes.GUILD_VOICE;
	const isLinkChannel = channel.type === ChannelTypes.GUILD_LINK;
	const isOwner = isGroupDM && channel.ownerId === currentUserId;
	const settingsGuildId = guild?.id ?? null;
	const channelOverride = UserGuildSettings.getChannelOverride(settingsGuildId, channel.id);
	const isMuted = channelOverride?.muted ?? false;
	const muteConfig = channelOverride?.mute_config;
	const mutedText = getMutedText(isMuted, muteConfig);
	const isFavorited = !!Favorites.getChannel(channel.id);
	const readState = ReadStates.get(channel.id);
	const hasUnread = readState.hasUnread();
	const canManageChannels = Permission.can(Permissions.MANAGE_CHANNELS, {
		channelId: channel.id,
		guildId: channel.guildId,
	});
	const canInvite = InviteUtils.canInviteToChannel(channel.id, channel.guildId);
	const developerMode = UserSettings.developerMode;
	const isPinned = channel.isPinned;
	return {
		isGroupDM,
		isDM,
		isTextChannel,
		isVoiceChannel,
		isLinkChannel,
		isOwner,
		isMuted,
		isFavorited,
		hasUnread,
		canManageChannels,
		canInvite,
		developerMode,
		isPinned,
		mutedText,
	};
}
export function useChannelMenuData(
	channel: Channel,
	guild: Guild | undefined,
	options: ChannelMenuDataOptions,
): ChannelMenuData {
	const {t, i18n} = useLingui();
	const {onClose, onOpenMuteSheet} = options;
	const leaveGroup = useLeaveGroup();
	const state = useMemo(() => getChannelMenuState(channel, guild), [channel, guild]);
	const handlers = useMemo(
		() => ({
			handleMarkAsRead: () => {
				ReadStateCommands.ack(channel.id, true, true);
				onClose();
			},
			handleToggleFavorite: () => {
				onClose();
				const guildId = channel.guildId ?? ME;
				const isFavorited = !!Favorites.getChannel(channel.id);
				if (isFavorited) {
					Favorites.removeChannel(channel.id);
					ToastCommands.createToast({type: 'success', children: t`Removed from favorites`});
				} else {
					Favorites.addChannel(channel.id, guildId, null);
					ToastCommands.createToast({type: 'success', children: t`Added to favorites`});
				}
			},
			handleInviteMembers: () => {
				onClose();
				ModalCommands.push(modal(() => <InviteModal channelId={channel.id} />));
			},
			handleCopyChannelLink: async () => {
				const link = buildChannelLink({
					guildId: channel.guildId,
					channelId: channel.id,
				});
				await TextCopyCommands.copy(i18n, link, true);
				ToastCommands.createToast({
					type: 'success',
					children: t`Channel link copied`,
				});
				onClose();
			},
			handleOpenChannelLink: () => {
				const channelUrl = channel.url;
				if (!channelUrl) return;
				const inviteCode = InviteUtils.findInvite(channelUrl);
				if (inviteCode) {
					void InviteCommands.openAcceptModal(inviteCode);
					onClose();
					return;
				}
				openExternalUrlWithWarning(channelUrl);
				onClose();
			},
			handleOpenMuteSheet: () => {
				onOpenMuteSheet?.();
			},
			handleNotificationSettings: () => {
				onClose();
				if (guild) {
					ModalCommands.push(modal(() => <GuildNotificationSettingsModal guildId={guild.id} />));
				}
			},
			handleChannelSettings: () => {
				onClose();
				ModalCommands.push(modal(() => <ChannelSettingsModal channelId={channel.id} />));
			},
			handleDeleteChannel: () => {
				onClose();
				ModalCommands.push(
					modal(() => (
						<ConfirmModal
							title={t`Delete Channel`}
							description={t`Are you sure you want to delete #${channel.name ?? channel.id}? This action cannot be undone.`}
							primaryText={t`Delete Channel`}
							primaryVariant="danger-primary"
							onPrimary={async () => {
								try {
									await ChannelCommands.remove(channel.id);
									ToastCommands.createToast({
										type: 'success',
										children: t`Channel deleted`,
									});
								} catch (error) {
									logger.error('Failed to delete channel:', error);
									ToastCommands.createToast({
										type: 'error',
										children: t`Failed to delete channel`,
									});
								}
							}}
						/>
					)),
				);
			},
			handleCopyChannelId: async () => {
				await TextCopyCommands.copy(i18n, channel.id, true);
				ToastCommands.createToast({
					type: 'success',
					children: t`Channel ID copied`,
				});
				onClose();
			},
			handleEditGroup: () => {
				onClose();
				ModalCommands.push(modal(() => <EditGroupModal channelId={channel.id} />));
			},
			handleShowInvites: () => {
				onClose();
				ModalCommands.push(modal(() => <GroupInvitesModal channelId={channel.id} />));
			},
			handlePinChannel: async () => {
				onClose();
				const isGroupDM = channel.type === ChannelTypes.GROUP_DM;
				try {
					await PrivateChannelCommands.pinDmChannel(channel.id);
					ToastCommands.createToast({
						type: 'success',
						children: isGroupDM ? t`Pinned group` : t`Pinned DM`,
					});
				} catch (error) {
					logger.error('Failed to pin:', error);
				}
			},
			handleUnpinChannel: async () => {
				onClose();
				const isGroupDM = channel.type === ChannelTypes.GROUP_DM;
				try {
					await PrivateChannelCommands.unpinDmChannel(channel.id);
					ToastCommands.createToast({
						type: 'success',
						children: isGroupDM ? t`Unpinned group` : t`Unpinned DM`,
					});
				} catch (error) {
					logger.error('Failed to unpin:', error);
				}
			},
			handleLeaveGroup: () => {
				onClose();
				leaveGroup?.(channel.id);
			},
			handleCloseDM: () => {
				onClose();
				ChannelCommands.remove(channel.id);
			},
			handleDebugChannel: () => {
				onClose();
				ModalCommands.push(modal(() => <ChannelDebugModal title={t`Channel Debug`} channel={channel} />));
			},
			handleResetNSFWAgreeState: () => {
				GuildNSFWAgree.revokeChannel(channel.id);
				onClose();
			},
		}),
		[channel, guild, i18n, leaveGroup, onClose, onOpenMuteSheet, t],
	);
	const groups = useMemo(() => {
		const menuGroups: Array<MenuGroupType> = [];
		if (state.isGroupDM) {
			const primaryItems: Array<MenuItemType> = [
				{
					icon: <EditGroupIcon size={20} />,
					label: t`Edit Group`,
					onClick: handlers.handleEditGroup,
				},
				state.isPinned
					? {
							icon: <PinIcon size={20} />,
							label: t`Unpin Group DM`,
							onClick: handlers.handleUnpinChannel,
						}
					: {
							icon: <PinIcon size={20} />,
							label: t`Pin Group DM`,
							onClick: handlers.handlePinChannel,
						},
			];
			if (state.isOwner) {
				primaryItems.push({
					icon: <SendInvitesIcon size={20} />,
					label: t`Invites`,
					onClick: handlers.handleShowInvites,
				});
			}
			menuGroups.push({items: primaryItems});
			const secondaryItems: Array<MenuItemType> = [
				{
					icon: <LeaveIcon size={20} />,
					label: t`Leave Group`,
					onClick: handlers.handleLeaveGroup,
					danger: true,
				},
			];
			if (state.developerMode) {
				secondaryItems.push({
					icon: <DebugChannelIcon size={20} />,
					label: t`Debug Channel`,
					onClick: handlers.handleDebugChannel,
				});
			}
			secondaryItems.push({
				icon: <CopyIdIcon size={20} />,
				label: t`Copy Channel ID`,
				onClick: handlers.handleCopyChannelId,
			});
			menuGroups.push({items: secondaryItems});
			return menuGroups;
		}
		if (state.isDM) {
			const items: Array<MenuItemType> = [
				state.isPinned
					? {
							icon: <PinIcon size={20} />,
							label: t`Unpin DM`,
							onClick: handlers.handleUnpinChannel,
						}
					: {
							icon: <PinIcon size={20} />,
							label: t`Pin DM`,
							onClick: handlers.handlePinChannel,
						},
				{
					icon: <CloseDMIcon size={20} />,
					label: t`Close DM`,
					onClick: handlers.handleCloseDM,
					danger: true,
				},
			];
			if (state.developerMode) {
				items.push({
					icon: <DebugChannelIcon size={20} />,
					label: t`Debug Channel`,
					onClick: handlers.handleDebugChannel,
				});
			}
			items.push({
				icon: <CopyIdIcon size={20} />,
				label: t`Copy Channel ID`,
				onClick: handlers.handleCopyChannelId,
			});
			menuGroups.push({items});
			return menuGroups;
		}
		if (guild && (state.isTextChannel || state.isVoiceChannel || state.isLinkChannel)) {
			if (state.hasUnread) {
				menuGroups.push({
					items: [
						{
							icon: <MarkAsReadIcon size={20} />,
							label: t`Mark as Read`,
							onClick: handlers.handleMarkAsRead,
						},
					],
				});
			}
			if (Accessibility.showFavorites) {
				menuGroups.push({
					items: [
						{
							icon: <FavoriteIcon filled={state.isFavorited} size={20} />,
							label: state.isFavorited ? t`Remove from Favorites` : t`Add to Favorites`,
							onClick: handlers.handleToggleFavorite,
						},
					],
				});
			}
			const inviteItems: Array<MenuItemType> = [];
			if (state.canInvite) {
				inviteItems.push({
					icon: <InviteIcon size={20} />,
					label: t`Invite People`,
					onClick: handlers.handleInviteMembers,
				});
			}
			if (state.isLinkChannel && channel.url) {
				inviteItems.push({
					icon: <OpenLinkIcon size={20} />,
					label: t`Open Link`,
					onClick: handlers.handleOpenChannelLink,
				});
			}
			inviteItems.push({
				icon: <CopyLinkIcon size={20} />,
				label: t`Copy Channel Link`,
				onClick: handlers.handleCopyChannelLink,
			});
			menuGroups.push({items: inviteItems});
			const notificationItems: Array<MenuItemType> = [];
			if (!state.isLinkChannel) {
				notificationItems.push({
					icon: state.isMuted ? <MuteIcon size={20} /> : <NotificationSettingsIcon size={20} />,
					label: state.isMuted ? t`Unmute Channel` : t`Mute Channel`,
					onClick: handlers.handleOpenMuteSheet,
				});
			}
			notificationItems.push({
				icon: <NotificationSettingsIcon size={20} />,
				label: t`Notification Settings`,
				onClick: handlers.handleNotificationSettings,
			});
			menuGroups.push({items: notificationItems});
			if (state.canManageChannels) {
				menuGroups.push({
					items: [
						{
							icon: <SettingsIcon size={20} />,
							label: t`Edit Channel`,
							onClick: handlers.handleChannelSettings,
						},
						{
							icon: <DeleteIcon size={20} />,
							label: t`Delete Channel`,
							onClick: handlers.handleDeleteChannel,
							danger: true,
						},
					],
				});
			}
			if (state.developerMode) {
				const debugItems: Array<MenuItemType> = [
					{
						icon: <DebugChannelIcon size={20} />,
						label: t`Debug Channel`,
						onClick: handlers.handleDebugChannel,
					},
				];
				if (GuildNSFWAgree.hasAgreedToChannel(channel.id)) {
					debugItems.push({
						icon: <DebugChannelIcon size={20} />,
						label: t`Reset NSFW Agree State`,
						onClick: handlers.handleResetNSFWAgreeState,
					});
				}
				menuGroups.push({items: debugItems});
			}
			menuGroups.push({
				items: [
					{
						icon: <CopyIdIcon size={20} />,
						label: t`Copy Channel ID`,
						onClick: handlers.handleCopyChannelId,
					},
				],
			});
			return menuGroups;
		}
		return [];
	}, [state, handlers, guild, t]);
	return {
		groups,
		handlers,
		state,
	};
}
